import {
  bearing,
  booleanCrosses,
  buffer,
  destination,
  Feature,
  featureCollection,
  getCoords,
  LineString,
  lineString,
  MultiLineString,
  MultiPolygon,
  multiPolygon,
  Point,
  polygon,
  Position,
} from "@turf/turf";
import { isNil } from "lodash";
import {
  compareAngles,
  countRows,
  getPointsAlongLine,
  solarAzimuthToBearing,
} from "../geo";
import { Block } from "../models/BlockModel";
import { makeIntersectionLine } from "../models/frameUtil";
import { PanelOrientationEnum } from "../pvComponents/projectParameters";
import { PanelFormValue } from "../pvComponents/types";
import { calculateModuleDimensionsFromPanelComponent } from "../pvComponents/utils";
import { isClose } from "../utils";

export interface TableColumnSpecs {
  numTiers: number;
  panelDimensions: [number, number];
  tierGap: number;
  columnGap: number;
  columnInnerDimensions: [number, number];
  columnOuterDimensions: [number, number];
  azimuth: number;
}

export function rect(
  origin: Position | Feature<Point>,
  width: number,
  height: number,
  bearing: number
) {
  const h = Math.sqrt(width * width + height * height) / 2;

  let angle = (Math.atan2(height, width) * 180) / Math.PI;
  angle = (angle < 0 ? angle + 360 : angle) + 90;

  if (!Array.isArray(origin)) {
    origin = origin.geometry.coordinates;
  }

  const points = [
    destination(origin, h, bearing + angle, { units: "meters" }),
    destination(origin, h, bearing - angle, { units: "meters" }),
    destination(origin, h, bearing + angle + 180, { units: "meters" }),
    destination(origin, h, bearing - (angle + 180), { units: "meters" }),
  ]
    .reverse()
    .map((p) => p.geometry.coordinates);

  const coordinates = points.concat([points[0]]);
  return polygon([coordinates]);
}

export function drawTableColumn(
  origin: Position | Feature<Point>,
  columnDrawSpecs: TableColumnSpecs,
  properties: any = {}
) {
  const [width, height] = columnDrawSpecs.panelDimensions;
  const { numTiers, tierGap } = columnDrawSpecs;
  const pitch = height + tierGap;
  const bearing = solarAzimuthToBearing(columnDrawSpecs.azimuth);
  const dist = (pitch * (numTiers - 1)) / 2;

  // Note: we add 0.1 when casting p1 because slight inaccuracy could
  // mean that getPointsAlongLine can't "fit" the final panel.
  const p0 = destination(origin, dist, bearing, { units: "meters" });
  const p1 = destination(origin, dist + 0.1, bearing + 180, {
    units: "meters",
  });

  const points = getPointsAlongLine([getCoords(p0), getCoords(p1)], pitch);
  const polygons = points.map((p) => rect(p, width, height, bearing));
  return multiPolygon(
    polygons.map((p) => p.geometry.coordinates),
    properties
  );
}

export function drawTable(
  line: Feature<LineString> | Position[],
  columnDrawSpecs: TableColumnSpecs,
  properties: any = {}
) {
  const [p0, p1] = getCoords(line);
  const perpendicular = isPerpendicular(
    bearing(p0, p1),
    solarAzimuthToBearing(columnDrawSpecs.azimuth)
  );
  const pitch = perpendicular
    ? columnDrawSpecs.columnOuterDimensions[0]
    : columnDrawSpecs.columnOuterDimensions[1];

  const columnCenters = getPointsAlongLine(line, pitch);
  const columns = columnCenters.map((p) => drawTableColumn(p, columnDrawSpecs));
  return multiPolygon(
    columns.flatMap((mp) => mp.geometry.coordinates),
    properties
  );
}

export function getBlockTableDrawOptions(
  block: Block
): TableColumnSpecs | undefined {
  const { pv_panel: panel, pv_table: table } = block;
  if (!panel || !table?.orientation_str || isNil(table?.azimuth)) {
    return undefined;
  }

  const { width, height } = calculateModuleDimensionsFromPanelComponent(
    panel as PanelFormValue,
    table.orientation_str as PanelOrientationEnum
  );

  if (isNil(width) || isNil(height)) {
    return undefined;
  }

  const azimuth = table.azimuth ?? 0;
  const numTiers = table.num_tiers ?? 1;
  const tierGap = table.tier_gap ?? 0;
  const columnGap = table.column_gap ?? 0;

  return {
    azimuth,
    numTiers,
    tierGap,
    columnGap,

    panelDimensions: [width, height],
    columnInnerDimensions: [
      width,
      height * numTiers + (numTiers - 1) * tierGap,
    ],
    columnOuterDimensions: [
      width + columnGap,
      height * numTiers + (numTiers - 1) * tierGap,
    ],
  };
}

export function isPerpendicular(bearingA: number, bearingB: number) {
  if (bearingA < 0) bearingA += 360;
  if (bearingB < 0) bearingB += 360;

  const delta = Math.abs(compareAngles(bearingA, bearingB));

  // tolerance can be low here;
  // we're already constraining to 90˚ increments around table azimuth
  return isClose(delta, 90, 1e-2) || isClose(delta, 270, 1e-2);
}

export function calcStats(
  tables: Array<Feature<MultiPolygon>>,
  columnDrawSpecs: TableColumnSpecs,
  numCols: number
) {
  const { azimuth, panelDimensions, tierGap } = columnDrawSpecs;
  const [, panelHeight] = panelDimensions;
  const panelRects = tables.flatMap((f) => f.geometry.coordinates);
  const moduleCount = panelRects.length;

  // TODO: Just use turf to cluster
  // tier pitch isn't right, and we may not have a correct row pitch
  const tierPitch = panelHeight + tierGap;
  const rowCount = countRows(tables, azimuth, tierPitch);
  const tableCount = Math.round(
    moduleCount / numCols / columnDrawSpecs.numTiers
  );

  return {
    rows: rowCount,
    tables: tableCount,
  };
}

export function getApproximateRowCount(
  lines: Feature<MultiLineString>,
  azimuth: number
) {
  const { coordinates } = lines.geometry;
  if (coordinates.length === 0) {
    return 0;
  }

  const collection = featureCollection(
    coordinates.map((coords) =>
      buffer(lineString(coords), 0.01, { units: "meters" })
    )
  );

  // TODO: make a generic version of this using row pitch and feature centers?
  const line = makeIntersectionLine(collection, solarAzimuthToBearing(azimuth));
  const intersectedLines = collection.features.filter((feature) => {
    return booleanCrosses(feature, line);
  });

  return intersectedLines.length;
}

export function getApproximateOutline(lines: Feature<MultiLineString>) {
  // TODO: do we want to consider panel/table dimensions?
  // For now we're just expanding lines to have 2D geometry
  return buffer(
    buffer(lines.geometry, 2, {
      units: "meters",
    }),
    -1,
    {
      units: "meters",
    }
  );
}
